// Product branding registry — the platform is BigAgri; each tenant kind sees
// the matching Big* product brand (a PACKHOUSE tenant sees BigPack, a FARM
// tenant BigFarm, etc.). The brand drives app chrome only — page titles, the
// OS theme-colour, the login/PWA identity, document provenance footers.
//
// IMPORTANT: the *product* brand is NOT the *tenant's* legal identity. Invoices,
// PPECB notes and letterheads keep the tenant's own company name (display_name /
// packhouse_config.legal_name) — KF49's documents still read "KF49 Citrus".
//
// Product names are fixed per kind, so this is a code constant (no DB table).
// Per-tenant white-label overrides (custom logo/colour) are a later phase.
// See docs/REBRAND-MIGRATION-PLAN.md.

import type { TenantKind } from './useActiveTenant'

export interface BrandDef {
  /** Full product name shown in chrome, e.g. 'BigPack'. */
  appName:    string
  /** PWA/short label. */
  shortName:  string
  /** One-line subtitle for login / headers. */
  tagline:    string
  /** Hex colour for the OS <meta theme-color> + PWA chrome. */
  themeColor: string
  /** Colour of the <BrandLoader/> B when `themeColor` is too dark to read on
   *  the app's gray-900 shell. Read it through `loaderColorOf`. */
  loaderColor?: string
  /** Platform billing/support contact shown to locked-out tenants. Only the
   *  PLATFORM brand sets this (the platform bills tenants, not the kind-brand). */
  supportEmail?: string
  /** Public marketing site the login screen points newcomers at ("New to
   *  BigAgri? See what it does"). Only the PLATFORM brand sets this: the site
   *  markets BigAgri itself, so a brand without one shows no link at all —
   *  the field's presence IS the decision (Login reads nothing else). */
  marketingUrl?: string
}

// The umbrella platform brand — used pre-auth (login), as the PWA install
// identity, and as document provenance ("Generated by BigAgri").
export const PLATFORM_BRAND: BrandDef = {
  appName:    'BigAgri',
  shortName:  'BigAgri',
  tagline:    'Farm-to-export platform',
  themeColor: '#16a34a',
  // TODO(WS2): swap to the platform support address once the neutral domain is
  // registered (see docs/REBRAND-MIGRATION-PLAN.md open items). Kept on the
  // current working inbox until then so locked-out tenants reach a real address.
  supportEmail: 'billing@kf49platform.com',
  // A crawlable link from the app's own domain is how search engines find the
  // new site (Thomas, ruled 2026-10-05).
  marketingUrl: 'https://info.bigagri.app',
}

export const KIND_BRANDS: Record<TenantKind, BrandDef> = {
  PACKHOUSE: { appName: 'BigPack',      shortName: 'BigPack',      tagline: 'Packhouse operations',     themeColor: '#2563eb' },
  FARM:      { appName: 'BigFarm',      shortName: 'BigFarm',      tagline: 'Orchard & field',          themeColor: '#16a34a' },
  AGENT:     { appName: 'BigAgent',     shortName: 'BigAgent',     tagline: 'Field advisory',           themeColor: '#7c3aed' },
  SUPPLIER:  { appName: 'BigSuppliers', shortName: 'BigSuppliers', tagline: 'Materials & delivery',     themeColor: '#d97706' },
  // EXPORTER → its own BigExport brand (Anchor Agri SA et al). Export desk:
  // loads register, stock-on-hand, packing lists, packouts.
  EXPORTER:  { appName: 'BigExport',    shortName: 'BigExport',    tagline: 'Export operations',        themeColor: '#0d9488' },
  // BUYER (importer) → the BigTrade marketplace (3b decision 2026-07-21:
  // the buyer's app IS the marketplace — browse, offer, track deals — so it
  // carries the marketplace brand, not a per-kind "BigBuyer").
  BUYER:     { appName: 'BigTrade',     shortName: 'BigTrade',     tagline: 'Fresh produce marketplace', themeColor: '#0891b2' },
  // TRANSPORTER (trucking company) → BigLogistics. Indigo matches the
  // tenant-switcher truck icon + the "On BigLogistics" badge.
  TRANSPORTER: { appName: 'BigLogistics', shortName: 'BigLogistics', tagline: 'Loads & fleet',          themeColor: '#4f46e5' },
  // HARVEST (harvest labour contractor) → BigHarvest. Citrus orange matches
  // the tenant-switcher icon (docs/specs/big-harvest.md, pilot: Johan).
  HARVEST:   { appName: 'BigHarvest',   shortName: 'BigHarvest',   tagline: 'Harvest teams & piecework', themeColor: '#ea580c' },
  // PAYROLL (system-of-record tenant for a business whose legal employers do
  // not line up with its sites) → BigPay, the name info.bigagri.app markets
  // it under (ruled 2026-09-29; it was BigPayroll). BigPay is the PAYROLL
  // product, not the future wage card — that needs a name of its own.
  // Teal-slate: adjacent to the labour teal without claiming a site brand,
  // because it owns no site.
  PAYROLL:   { appName: 'BigPay',       shortName: 'BigPay',       tagline: 'Workers & payroll',        themeColor: '#0f766e', loaderColor: '#14b8a6' },
  // SECURITY (security company) → BigSecurity. Slate matches the
  // tenant-switcher shield icon (docs/specs/bigsecurity-tenant.md).
  SECURITY:  { appName: 'BigSecurity',  shortName: 'BigSecurity',  tagline: 'Client sites & security',  themeColor: '#475569', loaderColor: '#94a3b8' },
  // PLATFORM (admin) → the umbrella brand.
  PLATFORM:  { appName: 'BigAgri',      shortName: 'BigAgri',      tagline: 'Platform admin',           themeColor: '#334155', loaderColor: '#94a3b8' },
}

export function loaderColorOf(brand: BrandDef): string {
  return brand.loaderColor ?? brand.themeColor
}

export function brandForKind(kind: TenantKind | null | undefined): BrandDef {
  if (!kind) return PLATFORM_BRAND
  return KIND_BRANDS[kind] ?? PLATFORM_BRAND
}
